import { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { GET_DELIVERY_RUN_HISTORY } from '../../graphql/delivery-run'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'
import { STORE_DOOR, addressOf, formatEta, stopNumbers } from '../../lib/delivery'
import {
  LATE_AFTER_MINUTES,
  MAX_HISTORY_DAYS,
  type DriverRollup,
  type Rollup,
  type RouteFigures,
  type StopFigures,
  driverKeyOf,
  localDayOf,
  rollup,
  rollupByDriver,
  routeFigures,
} from '../../lib/deliveryHistory'
import { apiTime, dateRangeToUtc, formatTime } from '../../lib/format'
import type { DeliveryRunDetail } from '../../types/order'
import {
  AccessDenied,
  EmptyData,
  QueryError,
  SectionTitle,
  StatCard,
  TH,
  TableLoading,
  firstOfMonth,
  isMissingFieldError,
  isValidYmd,
  parseYmd,
  toYmd,
} from './shared'

// Routes driven — the owner's overview of completed delivery routes (owner ask 2026-10-02: "see
// routes drivers did, filter by day / driver, and show the times to see how effective the
// planning was"). Contract: ../../API/docs/frontend/pos-delivery-run-contract.md § "Routes driven".
// One read per date window; the driver filter and every figure are client-side over the same
// rows (lib/deliveryHistory.ts). No polling: a report, not a board.

// ─── Formatting ───────────────────────────────────────────────────────────────

const clock = (iso: string | null): string => (iso ? formatTime(iso) : '—')

const minutes = (value: number | null): string => (value === null ? '—' : `${Math.round(value)} min`)

/** "+9 min" / "−3 min" / "0 min" — a typographic minus, so the column reads as signed. */
const signedMinutes = (value: number | null): string => {
  if (value === null) return '—'
  if (value > 0) return `+${value} min`
  if (value < 0) return `−${Math.abs(value)} min`
  return '0 min'
}

const lateLabel = (r: Rollup): string => (r.preorders > 0 ? `${r.preordersLate} of ${r.preorders}` : '—')

/** "Thu 1 Oct" — the reports section's en-GB, without the comma en-GB puts after the weekday. */
const dayHeading = (ymd: string): string =>
  parseYmd(ymd)
    .toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
    .replace(',', '')

// The terminal's LOCAL day. shared.today() is UTC and reads yesterday between 00:00 and 02:00 —
// wrong for a picker whose days are the local evenings routes left on.
const localToday = (): string => toYmd(new Date())

const localDaysAgo = (days: number): string => {
  const now = new Date()
  return toYmd(new Date(now.getFullYear(), now.getMonth(), now.getDate() - days))
}

// ─── Controls ─────────────────────────────────────────────────────────────────

const PRESET_CLASS = 'text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors'
const INPUT_CLASS =
  'text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-400'

/** Single-select driver filter, same look as the shared channel chips. null = All. */
const DriverFilterChips = ({
  drivers,
  value,
  onChange,
}: {
  drivers: DriverRollup[]
  value: string | null
  onChange: (key: string | null) => void
}) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide mr-1">Driver</span>
    {[{ key: null as string | null, name: 'All' }, ...drivers].map((driver) => (
      <button
        key={driver.key ?? 'all'}
        type="button"
        onClick={() => onChange(driver.key)}
        className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
          value === driver.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
        }`}
      >
        {driver.name}
      </button>
    ))}
  </div>
)

// ─── Per-driver table ─────────────────────────────────────────────────────────

const DriverTable = ({ drivers }: { drivers: DriverRollup[] }) => (
  <div>
    <SectionTitle>Per driver</SectionTitle>
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <TH>Driver</TH>
              <TH right>Routes</TH>
              <TH right>Stops</TH>
              <TH right>Avg on the road</TH>
              <TH right>Avg stops / route</TH>
              <TH right>Avg wait</TH>
              <TH right>Pre-orders late</TH>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {drivers.map((d) => (
              <tr key={d.key}>
                <td className="px-4 py-2.5 font-medium text-gray-900">{d.name}</td>
                <td className="px-4 py-2.5 text-right text-gray-700">{d.routes}</td>
                <td className="px-4 py-2.5 text-right text-gray-700">{d.stopsDelivered}</td>
                <td className="px-4 py-2.5 text-right text-gray-700">{minutes(d.avgOnRoadMin)}</td>
                <td className="px-4 py-2.5 text-right text-gray-700">
                  {d.avgStopsPerRoute === null ? '—' : d.avgStopsPerRoute.toFixed(1)}
                </td>
                <td className="px-4 py-2.5 text-right text-gray-700">{minutes(d.avgWaitMin)}</td>
                <td className="px-4 py-2.5 text-right text-gray-700">{lateLabel(d)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
)

// ─── Route row + stops ────────────────────────────────────────────────────────

/** Full literal classes: late red, otherwise green — the owner's 5 minutes ARE on time, so a
 *  "+3 min" reads as kept, not as a near miss. */
const vsPromiseClass = (s: StopFigures): string => {
  if (s.vsPromiseMin === null) return 'px-3 py-2 text-right text-gray-400'
  return s.late ? 'px-3 py-2 text-right font-medium text-red-700' : 'px-3 py-2 text-right font-medium text-emerald-700'
}

const STATUS_LABEL: Record<string, string> = { CANCELLED: 'Cancelled', REFUNDED: 'Refunded' }

const StopsTable = ({ route }: { route: RouteFigures }) => {
  const numbers = stopNumbers(route.run.stops)
  const back = formatEta(route.backAt)
  return (
    <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 uppercase tracking-wide">
              <th className="px-3 pb-2 text-left font-medium">#</th>
              <th className="px-3 pb-2 text-left font-medium">Order</th>
              <th className="px-3 pb-2 text-left font-medium">Address</th>
              <th className="px-3 pb-2 text-right font-medium">Promise</th>
              <th className="px-3 pb-2 text-right font-medium">Ready</th>
              <th className="px-3 pb-2 text-right font-medium">Waited</th>
              <th className="px-3 pb-2 text-right font-medium">Delivered</th>
              <th className="px-3 pb-2 text-right font-medium">Leg</th>
              <th className="px-3 pb-2 text-right font-medium whitespace-nowrap">vs promise</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {route.stops.map((s) => {
              const muted = s.settled === 'cancelled'
              return (
                <tr key={s.stop.id} className={muted ? 'text-gray-400' : 'text-gray-700'}>
                  <td className="px-3 py-2">{numbers.get(s.stop.id) ?? ''}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <p className={muted ? 'font-medium' : 'font-medium text-gray-900'}>
                      #{s.stop.order_number.slice(-5)}
                    </p>
                    {s.stop.customer_name && <p className="text-xs text-gray-400">{s.stop.customer_name}</p>}
                  </td>
                  <td className="px-3 py-2">{addressOf(s.stop).line1 || '—'}</td>
                  {muted ? (
                    // A cancelled or refunded stop was part of the route but nobody rode there:
                    // its status, no figures.
                    <td colSpan={6} className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide">
                      {STATUS_LABEL[s.stop.status] ?? s.stop.status}
                    </td>
                  ) : (
                    <>
                      <td className="px-3 py-2 text-right">{s.promise ? clock(s.promise) : 'ASAP'}</td>
                      <td className="px-3 py-2 text-right">{clock(s.readyAt)}</td>
                      <td className="px-3 py-2 text-right">{minutes(s.waitedAtStoreMin)}</td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900">{clock(s.deliveredAt)}</td>
                      <td className="px-3 py-2 text-right">{signedMinutes(s.legMin)}</td>
                      <td className={vsPromiseClass(s)}>{signedMinutes(s.vsPromiseMin)}</td>
                    </>
                  )}
                </tr>
              )
            })}
            <tr className="text-gray-500">
              <td colSpan={9} className="px-3 py-2 text-xs">
                Back at the store · {STORE_DOOR} · {back ? `${back} (estimate)` : 'no estimate'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="mt-3 space-y-0.5 text-xs text-gray-400">
        <p>Waited: the kitchen marked it ready → the route left the store.</p>
        <p>Leg: the previous door → this door; the first stop from the store.</p>
        <p>
          vs promise: delivered against the promised time, pre-orders only; late is more than{' '}
          {LATE_AFTER_MINUTES} min after it.
        </p>
        <p>Back at the store is an estimate — nothing records the return.</p>
      </div>
    </div>
  )
}

const RouteRow = ({
  route,
  expanded,
  onToggle,
}: {
  route: RouteFigures
  expanded: boolean
  onToggle: () => void
}) => {
  const back = formatEta(route.backAt)
  return (
    <div>
      {/* The whole row is the tap target — the POS runs on touchscreens, nothing hides behind hover. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left text-sm hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900">Route {route.run.id}</span>
        <span className="text-gray-700">{route.run.driver?.name ?? 'No driver'}</span>
        <span className="text-gray-700">departed {clock(route.departedAt)}</span>
        <span className="text-gray-700">last door {clock(route.lastDeliveredAt)}</span>
        <span className="text-gray-400">back {back ?? '—'}</span>
        <span className="text-gray-700">
          {route.run.stops_total} {route.run.stops_total === 1 ? 'stop' : 'stops'}
        </span>
        <span className="text-gray-700">{minutes(route.onRoadMin)} on the road</span>
        <ChevronDownIcon
          className={`ml-auto h-4 w-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      {expanded && <StopsTable route={route} />}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const DeliveryReportsPage = () => {
  const { hasPermission } = usePermissions()
  const canView = hasPermission(PERMISSIONS.STATS_DELIVERY_RUNS_VIEW)

  const [from, setFrom] = useState(localToday)
  const [to, setTo] = useState(localToday)
  const [driverKey, setDriverKey] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set())

  // The window in CALENDAR days (From and To inclusive), capped at the API's 31. Over the cap or
  // inverted → no query, so the API's refusal is not provoked on purpose. An incomplete date (an
  // input mid-edit) skips too — dateRangeToUtc must not see it. Calendar days, not elapsed ms:
  // the API measures 31 × 24 h on the UTC bounds, and 31 local days across the autumn clock
  // change span 745 h — so the page's own "This month" on 31 October must not refuse itself.
  // That one range the API still refuses with its own message, shown verbatim below (filed to
  // the API: count calendar days).
  const range = useMemo((): { variables: { from: string; to: string } | null; problem: string | null } => {
    if (!isValidYmd(from) || !isValidYmd(to)) return { variables: null, problem: null }
    const days = Math.round((parseYmd(to).getTime() - parseYmd(from).getTime()) / 86_400_000) + 1
    if (days < 1) return { variables: null, problem: 'From must be on or before To.' }
    if (days > MAX_HISTORY_DAYS) return { variables: null, problem: `Pick at most ${MAX_HISTORY_DAYS} days.` }
    const { date_from, date_to } = dateRangeToUtc(from, to)
    return { variables: { from: date_from, to: date_to }, problem: null }
  }, [from, to])

  const { data, loading, error } = useQuery<{ deliveryRunHistory: DeliveryRunDetail[] }>(
    GET_DELIVERY_RUN_HISTORY,
    {
      variables: range.variables ?? undefined,
      fetchPolicy: 'cache-and-network',
      skip: !canView || !range.variables,
    }
  )

  const runs = useMemo(() => data?.deliveryRunHistory ?? [], [data])
  const drivers = useMemo(() => rollupByDriver(runs), [runs])
  // A driver picked in another window who did not drive in this one falls back to All, rather
  // than leaving the page empty behind a chip that is no longer there.
  const activeKey = driverKey !== null && drivers.some((d) => d.key === driverKey) ? driverKey : null
  const filtered = useMemo(
    () => (activeKey === null ? runs : runs.filter((run) => driverKeyOf(run) === activeKey)),
    [runs, activeKey]
  )
  const summary = useMemo(() => rollup(filtered), [filtered])

  // Grouped by the LOCAL day each route left, newest day first and newest route first inside it.
  const days = useMemo(() => {
    const sorted = [...filtered].sort((a, b) => apiTime(b.started_at) - apiTime(a.started_at))
    const groups = new Map<string, DeliveryRunDetail[]>()
    for (const run of sorted) {
      const day = localDayOf(run.started_at) ?? ''
      groups.set(day, [...(groups.get(day) ?? []), run])
    }
    return [...groups.entries()].map(([day, dayRuns]) => ({
      day,
      routes: dayRuns.map(routeFigures),
      stops: dayRuns.reduce((sum, run) => sum + run.stops_total, 0),
    }))
  }, [filtered])

  if (!canView) return <AccessDenied message="You don't have permission to view routes driven." />

  const setRange = (start: string, end: string) => {
    setFrom(start)
    setTo(end)
  }

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const renderBody = () => {
    if (!range.variables) return null
    if (loading && !data) return <TableLoading />
    if (error) {
      return (
        <QueryError
          message={
            isMissingFieldError(error)
              ? "Routes driven needs the API's 2026-10-02 change (deliveryRunHistory, ready_at, delivered_at)."
              : error.message
          }
        />
      )
    }
    if (runs.length === 0) return <EmptyData />

    return (
      <>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard label="Routes" value={String(summary.routes)} />
          <StatCard label="Stops delivered" value={String(summary.stopsDelivered)} />
          <StatCard label="Avg on the road" value={minutes(summary.avgOnRoadMin)} sub="per route" />
          <StatCard
            label="Avg wait at the store"
            value={minutes(summary.avgWaitMin)}
            sub="ready → route left, per stop"
          />
          <StatCard
            label="Pre-orders late"
            value={lateLabel(summary)}
            sub={`> ${LATE_AFTER_MINUTES} min after the promised time`}
          />
        </div>

        {activeKey === null && drivers.length >= 2 && <DriverTable drivers={drivers} />}

        <div className="space-y-6">
          {days.map(({ day, routes, stops }) => (
            <div key={day || 'unknown'}>
              <div className="flex items-baseline gap-3 mb-2">
                <h2 className="text-sm font-semibold text-gray-900">{day ? dayHeading(day) : 'Unknown day'}</h2>
                <span className="text-xs text-gray-400">
                  {routes.length} {routes.length === 1 ? 'route' : 'routes'} · {stops}{' '}
                  {stops === 1 ? 'stop' : 'stops'}
                </span>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden divide-y divide-gray-100">
                {routes.map((route) => (
                  <RouteRow
                    key={route.run.id}
                    route={route}
                    expanded={expanded.has(route.run.id)}
                    onToggle={() => toggle(route.run.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Routes driven</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Completed delivery routes by day and driver — when each route left, when every door was
          reached, and what waited at the store.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
              From
            </label>
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className={INPUT_CLASS}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
              To
            </label>
            <input
              type="date"
              value={to}
              min={from}
              max={localToday()}
              onChange={(e) => setTo(e.target.value)}
              className={INPUT_CLASS}
            />
          </div>
          <div className="flex flex-wrap gap-2 ml-1">
            <button type="button" onClick={() => setRange(localToday(), localToday())} className={PRESET_CLASS}>
              Today
            </button>
            <button type="button" onClick={() => setRange(localDaysAgo(1), localDaysAgo(1))} className={PRESET_CLASS}>
              Yesterday
            </button>
            <button type="button" onClick={() => setRange(localDaysAgo(6), localToday())} className={PRESET_CLASS}>
              Last 7 days
            </button>
            <button type="button" onClick={() => setRange(firstOfMonth(), localToday())} className={PRESET_CLASS}>
              This month
            </button>
          </div>
        </div>
        {range.problem && <p className="text-sm text-amber-700">{range.problem}</p>}
        {range.variables && drivers.length > 0 && (
          <DriverFilterChips drivers={drivers} value={activeKey} onChange={setDriverKey} />
        )}
      </div>

      {renderBody()}
    </div>
  )
}

export default DeliveryReportsPage
